import { useTranslation } from 'react-i18next';
import {
  Box,
  styled,
  Typography as MuiTypography,
  useTheme,
} from '@mui/material';
import { IconExternalLink } from '@tabler/icons-react';

import { Tabs, Typography } from '@zendesk/forethought-elements';
import { FROM_NUMBER, NA, TO_NUMBER } from '../constants';
import AiQaChatDrawerTabContent from '../ConversationDrawer/AiQaChatDrawerTabContent';
import { ContextVariables } from '../ConversationDrawer/ContextVariables';
import { InlineData } from '../ConversationDrawer/InlineData';
import {
  ResolutionSummaryData,
  ResolutionTierInsightsData,
} from '../ConversationDrawer/ResolutionTierInsightsData';
import { SummaryCard } from '../ConversationDrawer/SummaryCard';
import { formatTimeSpent } from '../ConversationDrawer/utils';
import ConversationTranscript from '../ConversationTranscript';
import { TranscriptContainer } from '../ConversationTranscript/RevampConversationTranscript';
import { formatCSAT, formatTimestamp } from '../helpers';
import { useAiQaTabs } from '../hooks/useAiQaTabs';
import useGetCSATColor from '../hooks/useGetCSATColor';
import { useFetchConversationVoiceRecording } from './hooks';
import { RecordingPlayer } from './RecordingPlayer';
import VoiceTranscripts from './VoiceTranscripts';
import { BrandIcon } from 'src/components/brand-icon/BrandIcon';
import {
  AVG_RESPONSE_TIME_FAST,
  AVG_RESPONSE_TIME_SLOW,
  MID_RESPONSE_TIME_COLOR,
} from 'src/components/dashboard-pages/voice-insights/conversations/constants';
import { getDiscoverSentimentEmoji } from 'src/components/discover-sentiment-emoji/helpers';
import { VoiceBrandBadge } from 'src/components/voice-brand-badge';
import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import { useIsFeatureFlagEnabled } from 'src/hooks/hooks';
import { Conversation } from 'src/services/apiInterfaces';
import { useGetBrandsQuery } from 'src/services/brand/brandApi';
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter';

export const VoiceDrawerBody = ({
  selectedConversation,
}: {
  selectedConversation: Conversation;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const {
    additional_feedback,
    ai_qa_scores,
    brand_id,
    channel: conversationChannel,
    context_variables,
    conversation_id,
    csat_feedback,
    csat_resolve,
    csat_score,
    deflected,
    last_executed_intent_user_query,
    resolution_automated,
    resolution_tier,
    resolution_tier_insights,
    start_datetime,
    summary,
    tagged_zendesk_ticket_source_link,
    transcript,
    user_queries,
    voice_is_outbound,
    voice_is_web_call,
    voice_metrics: voiceMetrics,
    voice_outbound_answered_by,
    voice_outbound_engaged,
    voice_outbound_user_asked_followup,
    voice_transcripts,
    widget_ticket_id,
  } = selectedConversation;

  const { palette } = useTheme();
  const isVoiceCsatEnabled = useIsFeatureFlagEnabled('voice_sms_csat');
  const isResolutionUiEnabled = useIsFeatureFlagEnabled('resolution_ui');
  const showCsat = isVoiceCsatEnabled && !voice_is_outbound;
  const getCSATColor = useGetCSATColor();
  const { feature_flags: dashboardFeatureFlags = [] } = useOrgConfig();
  const isDisplaySummaryCardEnabled = dashboardFeatureFlags.includes(
    'display_summary_card',
  );
  const { selectedTab, setSelectedTab, showAiQaTab, tabs } =
    useAiQaTabs(ai_qa_scores);
  const { data: webCallBrands } = useGetBrandsQuery(
    { brandType: 'web-call' },
    { skip: !voice_is_web_call },
  );
  const webCallBrandIndex =
    webCallBrands?.findIndex(b => b.brand_id === brand_id) ?? -1;
  const webCallBrand =
    webCallBrandIndex >= 0 ? webCallBrands?.[webCallBrandIndex] : undefined;

  const channelAndMode: string = voice_is_web_call
    ? 'Web Call'
    : capitalizeFirstLetter(conversationChannel || '') +
      (voice_is_outbound ? ' - Outbound' : ' - Inbound');

  const sentiment = voiceMetrics?.sentiment;
  const callDuration = voiceMetrics?.call_duration;
  const avgResponseTime = voiceMetrics?.avg_response_time
    ? Math.round(voiceMetrics?.avg_response_time * 100) / 100
    : undefined;
  const shouldShowResolutionDetails =
    isResolutionUiEnabled &&
    Boolean(resolution_tier || resolution_tier_insights);

  const getColorForAvgResponseTime = (avgResponseTime: number | undefined) => {
    if (avgResponseTime === undefined) {
      return undefined;
    }

    if (avgResponseTime < AVG_RESPONSE_TIME_FAST) {
      return palette.colors.green[500];
    }

    if (avgResponseTime > AVG_RESPONSE_TIME_SLOW) {
      return palette.colors.red[500];
    }

    return MID_RESPONSE_TIME_COLOR;
  };

  const PHONE_NUMBER_KEYS = new Set([
    FROM_NUMBER,
    TO_NUMBER,
    'From Number',
    'To Number',
  ]);
  const filteredContextVariables = context_variables
    ? Object.fromEntries(
        Object.entries(context_variables).filter(
          ([key]) => !PHONE_NUMBER_KEYS.has(key),
        ),
      )
    : undefined;

  const { alertMessage, audioBlob, isLoadingConversationRecording } =
    useFetchConversationVoiceRecording(conversation_id);

  return (
    <Box
      sx={{
        p: '40px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          py: '6px',
        }}
      >
        <Typography variant='font24'>
          {t('voiceDrawerBody.conversationTitle', {
            direction: voice_is_outbound
              ? t('filterOptions.typeOutbound')
              : t('filterOptions.typeInbound'),
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '30px',
          py: '24px',
        }}
      >
        {/* Info section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <InlineData
            label={t('emailConversationDrawer.conversationId')}
            value={conversation_id}
          />
          {widget_ticket_id && (
            <InlineData
              label={t('voiceDrawerBody.helpdeskTicketId')}
              value={
                tagged_zendesk_ticket_source_link ? (
                  <a
                    href={tagged_zendesk_ticket_source_link}
                    rel='noreferrer'
                    target='_blank'
                  >
                    <Box alignItems='center' display='flex' gap='2px'>
                      <Typography
                        color={palette.colors.blue[600]}
                        variant='font12Medium'
                      >
                        {widget_ticket_id}
                      </Typography>
                      <IconExternalLink
                        color={palette.colors.blue[600]}
                        size={14}
                      />
                    </Box>
                  </a>
                ) : (
                  widget_ticket_id
                )
              }
            />
          )}
          {voice_is_outbound && (
            <InlineData
              label={t('emailConversationDrawer.eligible')}
              value={voice_outbound_answered_by === 'user' ? 'Yes' : 'No'}
            />
          )}
          {isResolutionUiEnabled && (
            <ResolutionSummaryData
              deflected={voice_is_outbound ? undefined : deflected}
              resolutionAutomated={resolution_automated}
              resolutionTier={resolution_tier}
              resolutionTierInsights={resolution_tier_insights}
            />
          )}
          {voice_is_outbound && (
            <InlineData
              color={
                voice_outbound_engaged
                  ? palette.colors.green[500]
                  : palette.colors.red[500]
              }
              label={t('filterOptions.engaged')}
              value={voice_outbound_engaged ? 'Yes' : 'No'}
            />
          )}
          {!voice_is_outbound && !isResolutionUiEnabled && (
            <InlineData
              label={t('drawerBody.deflectedLabel')}
              value={deflected ? 'True' : 'False'}
            />
          )}
        </Box>
        {isDisplaySummaryCardEnabled && summary && (
          <SummaryCard conversationId={conversation_id} summary={summary} />
        )}
        {/* Tabs section */}
        {showAiQaTab && (
          <Tabs
            a11yProps={index => ({
              'aria-controls': `conversation-drawer-tabpanel-${index}`,
              id: `conversation-drawer-tab-${index}`,
            })}
            aria-label={t('revampDrawerBody.conversationDrawerTabsAriaLabel')}
            onChange={(_, value) => setSelectedTab(value)}
            tabs={tabs}
            typographyVariant='font14'
            value={selectedTab}
          />
        )}
        {selectedTab === 0 && (
          <>
            {/* Chat section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <Typography variant='font11'>
                {t('summaryCard.summary')}
              </Typography>
              <Card>
                <CardSection gap='4px'>
                  <InlineData
                    label={t('voiceDrawerBody.timeOfCall')}
                    value={formatTimestamp(start_datetime + 'Z')}
                  />
                  <InlineData
                    label={t('voiceDrawerBody.talkTime')}
                    value={callDuration ? formatTimeSpent(callDuration) : NA}
                  />
                  <InlineData
                    label={t('revampDrawerBody.channelLabel')}
                    value={channelAndMode}
                  />
                  {isResolutionUiEnabled && (
                    <ResolutionSummaryData
                      deflected={voice_is_outbound ? undefined : deflected}
                      resolutionAutomated={resolution_automated}
                      resolutionTier={resolution_tier}
                      resolutionTierInsights={resolution_tier_insights}
                    />
                  )}
                  {voice_is_outbound && (
                    <InlineData
                      label={t('emailConversationDrawer.eligible')}
                      value={
                        voice_outbound_answered_by === 'user' ? 'Yes' : 'No'
                      }
                    />
                  )}
                  {!voice_is_outbound && !voice_is_web_call && (
                    <InlineData
                      label={t('voiceDrawerBody.source')}
                      value='Phone'
                    />
                  )}
                  {voice_is_web_call ? (
                    webCallBrand && (
                      <InlineData
                        label={t('voiceDrawerBody.brand')}
                        value={
                          <Box
                            alignItems='center'
                            borderRadius='4px'
                            display='flex'
                            gap={0.5}
                            height='24px'
                            maxWidth='100%'
                            overflow='hidden'
                            padding='4px 8px'
                            sx={{
                              bgcolor: palette.colors.slate[100],
                            }}
                            width='fit-content'
                          >
                            <BrandIcon
                              index={webCallBrandIndex}
                              variant='small'
                            />
                            <MuiTypography
                              noWrap
                              sx={{ color: palette.colors.grey[700] }}
                              variant='font12Medium'
                            >
                              {webCallBrand.brand_name}
                            </MuiTypography>
                          </Box>
                        }
                      />
                    )
                  ) : (
                    <>
                      <InlineData
                        label={t('voiceDrawerBody.callFrom')}
                        value={context_variables?.[FROM_NUMBER] || NA}
                      />
                      {(voice_is_outbound
                        ? context_variables?.[FROM_NUMBER]
                        : context_variables?.[TO_NUMBER]) && (
                        <InlineData
                          label={t('voiceDrawerBody.brand')}
                          value={
                            <VoiceBrandBadge
                              phoneNumbers={[
                                (voice_is_outbound
                                  ? context_variables?.[FROM_NUMBER]
                                  : context_variables?.[TO_NUMBER]) || NA,
                              ]}
                            />
                          }
                        />
                      )}
                      <InlineData
                        label={t('voiceDrawerBody.callTo')}
                        value={context_variables?.[TO_NUMBER] || NA}
                      />
                    </>
                  )}
                  {voice_is_outbound ? (
                    <InlineData
                      label={t('filterOptions.callAnswered')}
                      value={
                        voice_outbound_answered_by == 'user' ? 'Yes' : 'No'
                      }
                    />
                  ) : null}
                  {voice_is_outbound ? (
                    <InlineData
                      label={t('filterOptions.voicemailLeft')}
                      value={
                        voice_outbound_answered_by == 'voicemail' ? 'Yes' : 'No'
                      }
                    />
                  ) : null}
                  {voice_is_outbound ? (
                    <InlineData
                      label={t('filterOptions.engaged')}
                      value={voice_outbound_engaged ? 'Yes' : 'No'}
                    />
                  ) : null}

                  {voice_is_outbound ? (
                    <InlineData
                      label={t('voiceDrawerBody.withFollowUpAsk')}
                      value={voice_outbound_user_asked_followup ? 'Yes' : 'No'}
                    />
                  ) : null}

                  <InlineData
                    color={getColorForAvgResponseTime(avgResponseTime)}
                    label={t('voiceDrawerBody.avgResponseTimeLabel')}
                    value={avgResponseTime ? `${avgResponseTime}sec` : NA}
                  />
                  <InlineData
                    label={t('filterOptions.sentiment')}
                    value={
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: '4px',
                        }}
                      >
                        {sentiment && (
                          <img
                            src={getDiscoverSentimentEmoji(sentiment, 'medium')}
                          />
                        )}
                        <Typography variant='font12'>
                          {sentiment ? capitalizeFirstLetter(sentiment) : NA}
                        </Typography>
                      </Box>
                    }
                  />
                  {showCsat && (
                    <InlineData
                      color={getCSATColor(csat_score)}
                      label={t('drawerBody.csatLabel')}
                      value={formatCSAT(csat_score)}
                    />
                  )}
                </CardSection>

                <Divider />

                {filteredContextVariables &&
                  Object.keys(filteredContextVariables).length > 0 && (
                    <>
                      <ContextVariables
                        contextVariables={filteredContextVariables}
                      />
                      <Divider />
                    </>
                  )}

                <CardSection>
                  <Typography color={palette.colors.grey[600]} variant='font12'>
                    {t('voiceDrawerBody.userResponse')}
                  </Typography>
                  {user_queries ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {user_queries.map((query, index) => (
                        <i key={index}>
                          <Typography
                            color={palette.colors.blue[600]}
                            variant='font14'
                          >
                            {query}
                          </Typography>
                        </i>
                      ))}
                    </Box>
                  ) : (
                    <i>
                      <Typography
                        color={palette.colors.blue[600]}
                        variant='font14'
                      >
                        {t('common.quotedValue', {
                          value: last_executed_intent_user_query,
                        })}
                      </Typography>
                    </i>
                  )}
                </CardSection>

                <Divider />

                {shouldShowResolutionDetails && (
                  <>
                    <CardSection>
                      <ResolutionTierInsightsData
                        resolutionTier={resolution_tier}
                        resolutionTierInsights={resolution_tier_insights}
                      />
                    </CardSection>
                    <Divider />
                  </>
                )}

                {/* CSAT */}
                {showCsat && (
                  <CardSection>
                    <InlineData
                      color={getCSATColor(csat_score)}
                      label={t('drawerBody.csatLabel')}
                      value={formatCSAT(csat_score)}
                    />
                    <InlineData
                      label={t('csatSubmissionStep.reasonsForRating')}
                      value={csat_feedback?.join(', ') ?? NA}
                    />
                    <InlineData
                      label={t('voiceDrawerBody.additionalFeedback')}
                      value={additional_feedback ?? NA}
                    />
                    <InlineData
                      label={t('voiceDrawerBody.resolutionConfirmation')}
                      value={
                        typeof csat_resolve === 'boolean'
                          ? csat_resolve
                            ? 'Yes'
                            : 'No'
                          : NA
                      }
                    />
                  </CardSection>
                )}
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <Typography variant='font11'>
                {t('voiceDrawerBody.callTranscript')}
              </Typography>
              <TranscriptContainer>
                <RecordingPlayer
                  alertMessage={alertMessage}
                  audioBlob={audioBlob}
                  fileName={conversation_id}
                  isLoading={isLoadingConversationRecording}
                />
                {voice_transcripts ? (
                  <VoiceTranscripts
                    callStartTime={voiceMetrics?.call_start_time}
                    transcripts={voice_transcripts}
                  />
                ) : (
                  <ConversationTranscript
                    transcript={transcript}
                    variant='drawer'
                  />
                )}
              </TranscriptContainer>
            </Box>
          </>
        )}
        {selectedTab === 1 && showAiQaTab && ai_qa_scores && (
          <AiQaChatDrawerTabContent aiQaScores={ai_qa_scores} />
        )}
      </Box>
    </Box>
  );
};

const Divider = styled('hr')`
  border-width: 0;
  border-bottom-width: thin;
  border-color: ${({ theme }) => theme.palette.colors.slate[200]};
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const Card = styled(Box)`
  border: 1px solid ${({ theme }) => theme.palette.colors.slate[200]};
  border-radius: 8px;
  /* padding: 16px; */
  display: flex;
  flex-direction: column;
`;

const CardSection = styled(Box)<{ gap?: string }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  ${props => props.gap && `gap: ${props.gap}`}
`;
